import { NgStyle, UpperCasePipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PlayerGroupManagerService, PlayerProfile } from '@core/saves/player-group-manager.service';
import { SAVE_SERVICE } from '@core/saves/saves.interface';
import { PortalButtonComponent } from '../../../portal-button/portal-button.component';
import { PortalClassProfilesComponent } from '../../../portal-class-profiles/portal-class-profiles.component';
import { PortalPanelComponent } from '../../../portal-panel/portal-panel.component';
import { ACTIVATION_SERVICE } from '../activation.service.interface';
import { PluginListenerHandle } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { noop } from 'lodash';

@Component({
  imports: [PortalClassProfilesComponent, PortalPanelComponent, PortalButtonComponent],
  templateUrl: './profile-selector.component.html',
  styleUrls: ['./profile-selector.component.css'],
})
export class ProfileSelectorComponent implements OnInit, OnDestroy {

  protected profiles = signal<(PlayerProfile & { color: string })[]>([]);
  protected loading = signal(true);
  protected showConfirmDialog = signal(false);
  protected showResetConfirmDialog = signal(false);

  private readonly activationService = inject(ACTIVATION_SERVICE);
  private readonly router = inject(Router);
  private readonly playerGroupManager = inject(PlayerGroupManagerService);
  private readonly saveService = inject(SAVE_SERVICE);

  private backButtonSubscription?: PluginListenerHandle;

  constructor() {
    CapacitorApp.addListener('backButton', noop).then((r) => (this.backButtonSubscription = r));
  }

  async ngOnInit() {
    const playerGroupCode = this.activationService.getPlayerGroupCode();
    if (!playerGroupCode) {
      await this.router.navigate(['/select-player-group']);
      return;
    }

    const profiles = await this.playerGroupManager.getProfiles(playerGroupCode);
    this.profiles.set(
      profiles.map((profile) => ({
        ...profile,
        color: this.generateProfileColor(profile.nickname),
      })),
    );
    this.loading.set(false);
  }

  ngOnDestroy() {
    this.backButtonSubscription?.remove();
  }

  async selectProfile(playerCode: string) {
    this.loading.set(true);

    try {
      await this.saveService.login(playerCode);
      await this.router.navigate(['/']);
    } finally {
      this.loading.set(false);
    }
  }

  switchClass() {
    this.showConfirmDialog.set(true);
  }

  confirmSwitchClass() {
    this.activationService.deletePlayerGroupCode();
    this.router.navigate(['/select-player-group']);
  }

  cancelSwitchClass() {
    this.showConfirmDialog.set(false);
  }

  resetApp() {
    this.showResetConfirmDialog.set(true);
  }

  confirmResetApp() {
    this.activationService.deleteActivationData();
    this.showResetConfirmDialog.set(false);
    this.router.navigate(['/selectDeviceType']);
  }

  cancelResetApp() {
    this.showResetConfirmDialog.set(false);
  }

  private generateProfileColor(letter: string) {
    const charCode = letter.charCodeAt(0);

    const r = (charCode * 91) % 256;
    const g = (charCode * 76) % 256;
    const b = (charCode * 53) % 256;

    const toHex = (value: number): string => {
      const hex = value.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
}
